import Decimal from 'decimal.js';
import Cookies from 'js-cookie';

export const calculatePurchaseAmounts = (
  purchaseTableData,
  totalDiscountAmount,
  taxVat,
) => {
  const calculateTotals = (key, data) => {
    if (!Array.isArray(data) || !key) return 0;
    return data?.reduce((sum, item) => sum + (parseFloat(item?.[key]) || 0), 0);
  };

  const totalNetAmount = calculateTotals('netAmount', purchaseTableData) || 0;
  const totalTaxableAmount = totalNetAmount - totalDiscountAmount;

  // Apply proportional discount
  const purchaseArrayWithDiscountAmount = purchaseTableData.map((item) => {
    const netAmount = Number(item?.netAmount) || 0;
    const discountRatio = totalNetAmount !== 0 ? netAmount / totalNetAmount : 0;

    return {
      ...item,
      discountAmount: parseFloat(
        (discountRatio * totalDiscountAmount).toFixed(3),
      ),
    };
  });

  // VAT Free Calculations
  const vatFreePurchaseData = purchaseArrayWithDiscountAmount.filter(
    (item) => item?.vatPercent === 0,
  );
  const vatFreeDiscountAmount = calculateTotals(
    'discountAmount',
    vatFreePurchaseData,
  );
  const vatFreeNet =
    calculateTotals('netAmount', vatFreePurchaseData) - vatFreeDiscountAmount;
  const vatFreeFee = vatFreeNet * (taxVat?.luxury_tax_value || 0);
  const vatFreeFeeRounded = parseFloat(vatFreeFee.toFixed(3));
  const vatFreePurchaseAmount = parseFloat(
    (vatFreeNet + vatFreeFeeRounded).toFixed(3),
  );

  // Vatable Calculations
  const vatablePurchaseData = purchaseArrayWithDiscountAmount?.filter(
    (item) => item?.vatPercent === 13,
  );
  const vatableDiscountAmount = calculateTotals(
    'discountAmount',
    vatablePurchaseData,
  );
  const vatableNet = calculateTotals('netAmount', vatablePurchaseData);
  const discountedNet = vatableNet - vatableDiscountAmount;
  const rawAmount =
    discountedNet + discountedNet * (taxVat?.luxury_tax_value || 0);
  const vatablePurchaseAmount = parseFloat(rawAmount.toFixed(3));

  // VAT Amount
  const vatAmount = parseFloat(
    (vatablePurchaseAmount * (taxVat?.vat_value || 0)).toFixed(3),
  );

  return {
    totalTaxableAmount,
    purchaseArrayWithDiscountAmount,
    vatFreePurchaseAmount,
    vatFreeFee: vatFreeFeeRounded,
    vatablePurchaseAmount,
    vatAmount,
  };
};

export const calculateSalesAmounts = (tableData, totalDiscountAmount) => {
  const roundTo3 = (value) =>
    new Decimal(value || 0).toDecimalPlaces(3).toNumber();
  const toDecimal = (value) => new Decimal(value || 0);

  const calculateTotal = (data, key) => {
    if (!Array.isArray(data) || !key) return 0;
    const sum = data.reduce(
      (acc, item) => acc.plus(toDecimal(item?.[key])),
      new Decimal(0),
    );
    return roundTo3(sum);
  };

  const totalNetAmount = toDecimal(calculateTotal(tableData, 'netAmount'));

  const totalDiscount = toDecimal(totalDiscountAmount);
  const totalTaxableAmount = roundTo3(totalNetAmount.minus(totalDiscount));

  const itemsWithDiscount = tableData.map((item) => {
    const netAmount = toDecimal(item?.netAmount);
    const discountRatio = totalNetAmount.isZero()
      ? new Decimal(0)
      : netAmount.dividedBy(totalNetAmount);
    const discountAmount = roundTo3(discountRatio.times(totalDiscount));
    return {
      ...item,
      discountAmount,
    };
  });

  const itemsWithTax = itemsWithDiscount.map((item) => {
    const netAmount = toDecimal(item.netAmount);
    const discountAmount = toDecimal(item.discountAmount);
    const taxableAmount = netAmount.minus(discountAmount);
    const taxPercent = toDecimal(item.taxPercent);
    const calculatedTaxAmount = roundTo3(taxableAmount.times(taxPercent));

    return {
      ...item,
      calculatedTaxAmount,
      taxAmount: calculatedTaxAmount,
      taxableAmount: roundTo3(taxableAmount),
      netAmountWithTax: roundTo3(taxableAmount.plus(calculatedTaxAmount)),
    };
  });

  const totalTaxAmount = roundTo3(
    itemsWithTax.reduce(
      (acc, item) => acc.plus(toDecimal(item.calculatedTaxAmount)),
      new Decimal(0),
    ),
  );

  return {
    totalTaxableAmount,
    purchaseArrayWithDiscountAmount: itemsWithDiscount,
    dataWithTaxAmount: itemsWithTax,
    totalTaxAmount,
  };
};

export const calculateSalesAmountsWithTaxVat = (
  tableData,
  totalDiscountAmount,
  purchaseAmount,
) => {
  const roundTo3 = (value) =>
    new Decimal(value || 0).toDecimalPlaces(3).toNumber();
  const toDecimal = (value) => new Decimal(value || 0);
  const calculateTotal = (data, key) => {
    if (!Array.isArray(data) || !key) return 0;
    const sum = data.reduce(
      (acc, item) => acc.plus(toDecimal(item?.[key])),
      new Decimal(0),
    );
    return roundTo3(sum);
  };

  const totalNetAmount = toDecimal(calculateTotal(tableData, 'netAmount'));
  // .minus(purchaseAmount);
  const totalDiscount = toDecimal(totalDiscountAmount);

  const totalTaxableAmount = roundTo3(totalNetAmount.minus(totalDiscount));

  const itemsWithDiscount = tableData.map((item) => {
    // const itemWisePurchaseAmount = toDecimal(
    //   tableData?.length > 1 ? purchaseAmount / (tableData?.length - 1) : 0,
    // );

    // const netAmount = toDecimal(item?.netAmount - itemWisePurchaseAmount);
    const netAmount = toDecimal(item?.netAmount);
    const discountRatio = totalNetAmount.isZero()
      ? new Decimal(0)
      : netAmount.dividedBy(totalNetAmount);

    const discountAmount = roundTo3(discountRatio.times(totalDiscount));
    const itemWisePurchaseAmount = roundTo3(
      discountRatio.times(purchaseAmount),
    );
    const totalDiamondStoneAmountPerRow = (
      item?.finalData?.diamondStoneJson ||
      item?.diamondStones ||
      []
    ).reduce((sum, stone) => {
      return sum + (stone.amount || 0);
    }, 0);

    return {
      ...item,
      purchaseAmount: itemWisePurchaseAmount || 0,
      discountAmount,
      totalDsPerRow: roundTo3(totalDiamondStoneAmountPerRow),
    };
  });

  let ngCr = new Decimal(0);
  // Add original index to track order
  const itemsWithIndex = itemsWithDiscount.map((item, index) => ({
    ...item,
    originalIndex: index,
  }));

  // Sort by netAmount for calculation
  const itemsWithTax = itemsWithIndex
    .sort((a, b) => a.netAmount - b.netAmount)
    .map((item) => {
      // const dAmount = toDecimal(item?.diamondWeight)
      //   .div(100)
      //   .times(item?.ratePerPurity);
      // const sAmount = toDecimal(item?.stoneWeight)
      //   .div(100)
      //   .times(item?.ratePerPurity);
      const netAmount = toDecimal(item.netAmount);
      // const netWeightWithWastage = toDecimal(item.netWeightWithWastage)
      //   .plus(toDecimal(item.stoneWeight))
      //   .plus(toDecimal(item?.diamondWeight));
      // const netAmount = netWeightWithWastage
      //   .div(toDecimal(11.664))
      //   .times(toDecimal(item?.ratePerPurity));
      // const netWIthWastage = toDecimal(item.netWithWastage);
      //
      const discountAmount = toDecimal(item.discountAmount);
      const purchaseAmount = toDecimal(item.purchaseAmount);
      const makingAmount = toDecimal(item?.makingAmount);
      const extraAmount = toDecimal(
        item?.extraAmount || item?.finalData?.detailsJson?.[0]?.extraAmount,
      );
      const isFixed = item?.finalData?.detailsJson?.[0]?.fixedAmount === 'yes';
      const totalDiamondStoneAmountPerRow = (
        item?.finalData?.diamondStoneJson ||
        item?.diamondStones ||
        []
      ).reduce((sum, stone) => {
        return sum + (stone.amount || 0);
      }, 0);

      const calculatableNetAmount = isFixed
        ? netAmount
        : netAmount.minus(totalDiamondStoneAmountPerRow);

      let taxableAmount = calculatableNetAmount
        .minus(discountAmount)
        .minus(purchaseAmount)
        // .minus(makingAmount)
        // .minus(extraAmount)
        // .minus(totalDiamondStoneAmountPerRow)
        .minus(ngCr);

      if (taxableAmount.isNegative()) {
        ngCr = ngCr.plus(taxableAmount.abs());
        taxableAmount = new Decimal(0);
      } else {
        ngCr = new Decimal(0);
      }

      const taxPercent = toDecimal(item.taxPercent);
      const vatPercent = toDecimal(item.vatPercent);
      const vatAmountT = item?.totalDsPerRow * vatPercent;
      const calculatedVatAmount = roundTo3(vatAmountT);
      const calculatedTaxAmount = roundTo3(taxableAmount.times(taxPercent));
      // const netValue = roundTo3(
      //   taxableAmount.plus(calculatedTaxAmount).plus(calculatedVatAmount),
      // );
      // const netValue = roundTo3(
      //   taxableAmount.plus(calculatedTaxAmount).plus(calculatedVatAmount),
      // );
      const netValue = roundTo3(
        netAmount
          .plus(calculatedTaxAmount)
          .plus(calculatedVatAmount)
          .minus(discountAmount),
      );

      return {
        ...item,
        calculatedTaxAmount,
        taxAmount: calculatedTaxAmount > 0 ? calculatedTaxAmount : 0,
        vatAmount: calculatedVatAmount > 0 ? calculatedVatAmount : 0,
        taxableAmount: taxableAmount > 0 ? roundTo3(taxableAmount) : 0,
        netAmountWithTax:
          taxableAmount > 0
            ? roundTo3(taxableAmount.plus(calculatedTaxAmount))
            : 0,
        netAmountWithTaxPlusVat: netValue < 0 ? 0 : netValue,
        subTotal: item?.netAmount,
      };
    })
    // Restore original order
    .sort((a, b) => a.originalIndex - b.originalIndex)
    // Remove the temporary index property
    .map(({ originalIndex, ...item }) => item);

  // const totalTaxAmount = roundTo3(
  //   itemsWithTax.reduce(
  //     (acc, item) => acc.plus(toDecimal(item.calculatedTaxAmount)),
  //     new Decimal(0),
  //   ),
  // );

  const totalTaxAmount = roundTo3(
    itemsWithTax.reduce(
      (acc, item) => acc.plus(toDecimal(item.taxAmount)),
      new Decimal(0),
    ),
  );

  const totalVatAmount = roundTo3(
    itemsWithTax.reduce(
      (acc, item) => acc.plus(toDecimal(item.vatAmount)),
      new Decimal(0),
    ),
  );

  const totalTaxableAmountToUse = roundTo3(
    itemsWithTax?.reduce(
      (acc, item) => acc.plus(toDecimal(item.taxableAmount)),
      new Decimal(0),
    ),
  );

  return {
    totalTaxableAmount: totalTaxableAmountToUse,
    purchaseArrayWithDiscountAmount: itemsWithDiscount,
    dataWithTaxAmount: itemsWithTax,
    totalTaxAmount,
    totalVatAmount,
  };
};

export const calculateSalesAmountsWithTaxVatV2 = (
  tableData,
  totalDiscountAmount,
  purchaseAmount,
  receiveFromPartyPureWeight,
) => {
  const roundTo3 = (value) =>
    new Decimal(value || 0).toDecimalPlaces(3).toNumber();
  const toDecimal = (value) => new Decimal(value || 0);
  const calculateTotal = (data, key) => {
    if (!Array.isArray(data) || !key) return 0;
    const sum = data.reduce(
      (acc, item) => acc.plus(toDecimal(item?.[key])),
      new Decimal(0),
    );
    return roundTo3(sum);
  };
  const totalNetAmount = toDecimal(calculateTotal(tableData, 'netAmount'));
  const totalDiscount = toDecimal(totalDiscountAmount);
  const totalTaxableAmount = roundTo3(totalNetAmount.minus(totalDiscount));
  const itemsWithDiscount = tableData.map((item) => {
    const netAmount = toDecimal(item?.netAmount);
    const discountRatio = totalNetAmount.isZero()
      ? new Decimal(0)
      : netAmount.dividedBy(totalNetAmount);

    const discountAmount = roundTo3(discountRatio.times(totalDiscount));
    const itemWisePurchaseAmount = roundTo3(
      discountRatio.times(purchaseAmount),
    );
    const totalDiamondStoneAmountPerRow = (
      item?.finalData?.diamondStoneJson ||
      item?.diamondStones ||
      []
    ).reduce((sum, stone) => {
      return sum + (stone.amount || 0);
    }, 0);

    return {
      ...item,
      purchaseAmount: itemWisePurchaseAmount || 0,
      discountAmount,
      totalDsPerRow: roundTo3(totalDiamondStoneAmountPerRow),
    };
  });

  let ngCr = new Decimal(0);
  // Add original index to track order
  const itemsWithIndex = itemsWithDiscount.map((item, index) => ({
    ...item,
    originalIndex: index,
  }));

  // Sort by netAmount for calculation
  const itemsWithTax = itemsWithIndex
    .sort((a, b) => a.netAmount - b.netAmount)
    .map((item) => {
      const netAmount = toDecimal(item.netAmount);

      const discountAmount = toDecimal(item.discountAmount);
      const purchaseAmount = toDecimal(item.purchaseAmount);
      const makingAmount = toDecimal(item?.makingAmount);
      const extraAmount = toDecimal(
        item?.extraAmount || item?.finalData?.detailsJson?.[0]?.extraAmount,
      );
      const isFixed = item?.finalData?.detailsJson?.[0]?.fixedAmount === 'yes';
      const totalDiamondStoneAmountPerRow = (
        item?.finalData?.diamondStoneJson ||
        item?.diamondStones ||
        []
      ).reduce((sum, stone) => {
        return sum + (stone.amount || 0);
      }, 0);

      const calculatableNetAmount = isFixed
        ? netAmount
        : netAmount.minus(totalDiamondStoneAmountPerRow);

      let taxableAmount = calculatableNetAmount
        .minus(discountAmount)
        .minus(purchaseAmount)
        .minus(ngCr);

      if (taxableAmount.isNegative()) {
        ngCr = ngCr.plus(taxableAmount.abs());
        taxableAmount = new Decimal(0);
      } else {
        ngCr = new Decimal(0);
      }

      const taxPercent = toDecimal(item.taxPercent);
      const vatPercent = toDecimal(item.vatPercent);
      const vatAmountT = item?.totalDsPerRow * vatPercent;
      const calculatedVatAmount = roundTo3(vatAmountT);
      const calculatedTaxAmount = roundTo3(taxableAmount.times(taxPercent));

      const netValue = roundTo3(
        netAmount
          .plus(calculatedTaxAmount)
          .plus(calculatedVatAmount)
          .minus(discountAmount),
      );

      return {
        ...item,
        calculatedTaxAmount,
        taxAmount: calculatedTaxAmount > 0 ? calculatedTaxAmount : 0,
        vatAmount: calculatedVatAmount > 0 ? calculatedVatAmount : 0,
        vatableAmount: roundTo3(totalDiamondStoneAmountPerRow || 0),
        taxableAmount: taxableAmount > 0 ? roundTo3(taxableAmount) : 0,
        netAmountWithTax:
          taxableAmount > 0
            ? roundTo3(taxableAmount.plus(calculatedTaxAmount))
            : 0,
        netAmountWithTaxPlusVat: netValue < 0 ? 0 : netValue,
        subTotal: item?.netAmount,
      };
    })
    .sort((a, b) => a.originalIndex - b.originalIndex)
    .map(({ originalIndex, ...item }) => item);

  let itemsWithReceivePureWeight = itemsWithTax;
  if (receiveFromPartyPureWeight > 0) {
    let extraReceiveWeight = new Decimal(receiveFromPartyPureWeight);
    const itemsAfterReceive = tableData.map((item) => {
      const makingAmount = roundTo3(item?.makingAmount);
      const roundOff = roundTo3(item?.finalData?.detailsJson?.[0]?.roundOff);

      const rate = toDecimal(item.rate || 0);
      const netWeightWithWastage = toDecimal(item?.netWithWastage || 0);
      const isFixed = item?.finalData?.detailsJson?.[0]?.fixedAmount === 'yes';
      const totalDiamondStoneAmountPerRow = (
        item?.finalData?.diamondStoneJson ||
        item?.diamondStones ||
        []
      ).reduce((sum, stone) => {
        return sum + (stone.amount || 0);
      }, 0);

      let weightedAmount = 0;
      let receiveAmount = 0;
      if (netWeightWithWastage.greaterThanOrEqualTo(extraReceiveWeight)) {
        const subWeight = netWeightWithWastage.minus(extraReceiveWeight);
        weightedAmount = roundTo3(subWeight.times(rate));
        receiveAmount += weightedAmount;
        extraReceiveWeight = new Decimal(0);
      } else if (netWeightWithWastage.lessThan(extraReceiveWeight)) {
        const subWeight = extraReceiveWeight.minus(netWeightWithWastage);
        weightedAmount = new Decimal(0);
        receiveAmount += roundTo3(subWeight.times(rate));
        extraReceiveWeight = subWeight;
      }

      const netAmount = roundTo3(
        (weightedAmount || 0) + (makingAmount || 0) + (roundOff || 0),
      );

      const calculatableNetAmount = isFixed ? item.netAmount : netAmount;

      return {
        ...item,
        calculatableNetAmount: calculatableNetAmount,
        vatableAmount: roundTo3(totalDiamondStoneAmountPerRow || 0),
        subTotal: item?.netAmount,
        receiveAmount: receiveAmount > 0 ? receiveAmount : 0,
      };
    });
    const calculatableTotalNetAmount = toDecimal(
      itemsAfterReceive.reduce(
        (acc, item) => acc + item?.calculatableNetAmount,
        0,
      ),
    );
    itemsWithReceivePureWeight = itemsAfterReceive?.map((item) => {
      const calculatableNetAmount = toDecimal(item?.calculatableNetAmount);
      const discountRatio =
        calculatableNetAmount !== 0
          ? calculatableNetAmount.div(calculatableTotalNetAmount)
          : 0;
      const discountAmount = toDecimal(discountRatio * totalDiscountAmount);
      const taxPercent = toDecimal(item.taxPercent);

      const taxableAmount = calculatableNetAmount.minus(discountAmount);

      const calculatedTaxAmount = roundTo3(taxableAmount.times(taxPercent));

      const vatPercent = toDecimal(item.vatPercent);
      const vatableAmount = toDecimal(item?.vatableAmount);
      const calculatedVatAmount = vatableAmount.times(vatPercent);

      const netValue = roundTo3(
        calculatableNetAmount
          .minus(discountAmount)
          .plus(calculatedTaxAmount)
          .plus(calculatedVatAmount),
      );
      return {
        ...item,
        discountAmount,
        taxAmount: calculatedTaxAmount > 0 ? calculatedTaxAmount : 0,
        taxableAmount: taxableAmount > 0 ? roundTo3(taxableAmount) : 0,
        vatAmount: calculatedVatAmount > 0 ? calculatedVatAmount : 0,
        netAmountWithTax:
          taxableAmount > 0
            ? roundTo3(
                taxableAmount.plus(calculatedTaxAmount).minus(discountAmount),
              )
            : 0,
        netAmountWithTaxPlusVat: netValue < 0 ? 0 : netValue,
      };
    });
  }

  const totalTaxAmount = roundTo3(
    itemsWithReceivePureWeight.reduce(
      (acc, item) => acc.plus(toDecimal(item.taxAmount)),
      new Decimal(0),
    ),
  );

  const totalVatAmount = roundTo3(
    itemsWithReceivePureWeight.reduce(
      (acc, item) => acc.plus(toDecimal(item.vatAmount)),
      new Decimal(0),
    ),
  );

  const totalTaxableAmountToUse = roundTo3(
    itemsWithReceivePureWeight?.reduce(
      (acc, item) => acc.plus(toDecimal(item.taxableAmount)),
      new Decimal(0),
    ),
  );

  const totalReceiveAmount = roundTo3(
    itemsWithReceivePureWeight?.reduce(
      (acc, item) => acc.plus(toDecimal(item.receiveAmount)),
      new Decimal(0),
    ),
  );

  return {
    totalTaxableAmount: totalTaxableAmountToUse,
    purchaseArrayWithDiscountAmount: itemsWithDiscount,
    dataWithTaxAmount: itemsWithReceivePureWeight,
    totalTaxAmount,
    totalVatAmount,
    totalReceiveAmount: totalReceiveAmount,
  };
};

export const roundTo3 = (value) =>
  new Decimal(value || 0).toDecimalPlaces(3).toNumber();
export const roundTo2 = (value) =>
  new Decimal(value || 0).toDecimalPlaces(2).toNumber();
export const toDecimal = (value) => new Decimal(value || 0);

// ------------------------------------------------------
// DISCOUNT %  <->  AMOUNT (base = total making amount)
// ------------------------------------------------------
export const discountPercentFromAmount = (amount, totalMakingAmount) => {
  const making = toDecimal(totalMakingAmount);
  if (making.isZero()) return 0;
  return roundTo2(toDecimal(amount).dividedBy(making).times(100));
};

export const discountAmountFromPercent = (percent, totalMakingAmount) =>
  roundTo2(
    toDecimal(percent).times(toDecimal(totalMakingAmount)).dividedBy(100),
  );

export const calculateTotal = (data, key) => {
  if (!Array.isArray(data) || !key) return 0;
  const sum = data.reduce(
    (acc, item) => acc.plus(toDecimal(item?.[key])),
    new Decimal(0),
  );
  return roundTo3(sum);
};

// ------------------------------------------------------
// UTILITY FUNCTION
// ------------------------------------------------------

// const toDec = (v) => new Decimal(v || 0);

// const getDiamondStoneTotal = (item) => {
//   const stones = item?.finalData?.diamondStoneJson || item?.diamondStones || [];
//   return stones.reduce((sum, s) => sum + (s.amount || 0), 0);
// };

// export const calculateSilverThreshold = (items, silverThreshold) => {
//   const totalSilverSubTotal =
//     items
//       ?.filter(
//         (item) =>
//           item?.itemType?.toLowerCase() === 'silver' ||
//           item?.itemTypeName?.toLowerCase() === 'silver',
//       )
//       ?.reduce(
//         (acc, item) =>
//           acc.plus(
//             item?.finalData?.detailsJson?.[0]?.subTotal || item?.subTotal || 0,
//           ),
//         new Decimal(0),
//       ) || new Decimal(0);

//   // const totalSilverItemExtraAmount =
//   //   items
//   //     ?.filter(
//   //       (item) =>
//   //         item?.itemType?.toLowerCase() === 'silver' ||
//   //         item?.itemTypeName?.toLowerCase() === 'silver',
//   //     )
//   //     ?.reduce(
//   //       (acc, item) =>
//   //         acc.plus(
//   //           item?.finalData?.detailsJson?.[0]?.extraAmount ||
//   //             item?.extraAmount ||
//   //             0,
//   //         ),
//   //       new Decimal(0),
//   //     ) || new Decimal(0);

//   const totalSilverDiamondStoneAmount =
//     items
//       ?.filter(
//         (item) =>
//           item?.itemType?.toLowerCase() === 'silver' ||
//           item?.itemTypeName?.toLowerCase() === 'silver',
//       )
//       ?.reduce(
//         (acc, item) => acc.plus(getDiamondStoneTotal(item)),
//         new Decimal(0),
//       ) || new Decimal(0);

//   return {
//     isSilverNetGreaterThanThreshold: totalSilverSubTotal
//       // .minus(totalSilverItemExtraAmount)
//       .minus(totalSilverDiamondStoneAmount)
//       .greaterThanOrEqualTo(silverThreshold),
//     totalSilverNet: totalSilverSubTotal,
//   };
// };

// const distributeDiscountsPurchaseMode = (
//   items,
//   totalDiscount,
//   purchaseAmount,
//   silverThreshold,
// ) => {
//   const totalNet = items.reduce(
//     (acc, it) => acc.plus(toDec(it.netAmount)),
//     new Decimal(0),
//   );

//   const totalDiamondStoneAmount = items?.reduce((acc, item) => {
//     const diamondStoneJson =
//       item?.diamondStones || item?.finalData?.diamondStoneJson || [];
//     const totalAmount = diamondStoneJson.reduce(
//       (acc, it) => acc.plus(toDec(it?.amount)),
//       new Decimal(0),
//     );
//     return acc.plus(totalAmount);
//   }, new Decimal(0));

//   const { isSilverNetGreaterThanThreshold, totalSilverNet } =
//     calculateSilverThreshold(items, silverThreshold);

//   const calculatableTotalNetForDiscount = toDec(totalNet);

//   const itemsWithDiscount = items.map((item) => {
//     const net = toDec(item.netAmount);

//     const diamondStoneJson =
//       item?.diamondStones || item?.finalData?.diamondStoneJson || [];
//     const dsAmount = diamondStoneJson.reduce(
//       (acc, it) => acc.plus(toDec(it?.amount)),
//       new Decimal(0),
//     );

//     const calculatableItemNetForDiscount = net || new Decimal(0);

//     // let ratioForPurchase = calculatableTotalNetForPurchase.isZero()
//     //   ? new Decimal(0)
//     //   : calculatableItemNetForPurchase.div(calculatableTotalNetForPurchase);

//     let ratioForDiscount = calculatableTotalNetForDiscount.isZero()
//       ? new Decimal(0)
//       : calculatableItemNetForDiscount.div(calculatableTotalNetForDiscount);

//     return {
//       ...item,
//       totalDsPerRow: roundTo3(getDiamondStoneTotal(item)),
//       discountAmount: roundTo3(ratioForDiscount.times(totalDiscount)),
//       // purchaseAmount: roundTo3(ratioForPurchase.times(purchaseAmount)),
//     };
//   });

//   // const calculatableTotalNetForPurchase = toDec(
//   //   isSilverNetGreaterThanThreshold
//   //     ? totalNet.minus(totalDiamondStoneAmount)
//   //     : totalNet
//   //         .minus(totalSilverNet)
//   //         .minus(totalDiamondStoneAmount)
//   //         .minus(totalDiscount),
//   // );

//   const calculatableTotalNetForPurchase = itemsWithDiscount.reduce(
//     (acc, item) => {
//       const isSilver =
//         item?.itemType?.toLowerCase() === 'silver' ||
//         item?.itemTypeName?.toLowerCase() === 'silver';
//       const net = toDec(item.netAmount);
//       const discount = toDec(item?.discountAmount || 0);
//       const diamondStoneJson =
//         item?.diamondStones || item?.finalData?.diamondStoneJson || [];
//       const dsAmount = diamondStoneJson.reduce(
//         (acc, it) => acc.plus(toDec(it?.amount)),
//         new Decimal(0),
//       );
//       const baseNet = net.minus(dsAmount).minus(discount);

//       const calculatableItemNetForPurchase =
//         isSilver && !isSilverNetGreaterThanThreshold ? new Decimal(0) : baseNet;

//       return acc.plus(calculatableItemNetForPurchase);
//     },
//     new Decimal(0),
//   );

//   // const calculatableItemNetForPurchase =
//   //   item?.itemType?.toLowerCase() === 'silver' ||
//   //   item?.itemTypeName?.toLowerCase() === 'silver'
//   //     ? isSilverNetGreaterThanThreshold
//   //       ? net.minus(dsAmount)
//   //       : new Decimal(0)
//   //     : net.minus(dsAmount);

//   const itemsWithPurchase = itemsWithDiscount?.map((item) => {
//     const isSilver =
//       item?.itemType?.toLowerCase() === 'silver' ||
//       item?.itemTypeName?.toLowerCase() === 'silver';
//     const net = toDec(item.netAmount);
//     const discount = toDec(item?.discountAmount || 0);
//     const diamondStoneJson =
//       item?.diamondStones || item?.finalData?.diamondStoneJson || [];
//     const dsAmount = diamondStoneJson.reduce(
//       (acc, it) => acc.plus(toDec(it?.amount)),
//       new Decimal(0),
//     );
//     const baseNet = net.minus(dsAmount).minus(discount);

//     const calculatableItemNetForPurchase =
//       isSilver && !isSilverNetGreaterThanThreshold ? new Decimal(0) : baseNet;

//     let ratioForPurchase = calculatableTotalNetForPurchase.isZero()
//       ? new Decimal(0)
//       : calculatableItemNetForPurchase.div(calculatableTotalNetForPurchase);

//     return {
//       ...item,
//       purchaseAmount: roundTo3(ratioForPurchase.times(purchaseAmount)),
//     };
//   });

//   return itemsWithPurchase;
// };

// const calculateTaxAndVat = (items, silverThreshold) => {
//   let carryForward = new Decimal(0);

//   const { isSilverNetGreaterThanThreshold } = calculateSilverThreshold(
//     items,
//     silverThreshold,
//   );

//   return items
//     .map((item, i) => ({ ...item, _idx: i }))
//     .sort((a, b) => a.netAmount - b.netAmount)
//     .map((item) => {
//       const net = toDec(item?.netAmount);
//       const discount = toDec(item?.discountAmount);
//       const purchaseA = toDec(item?.purchaseAmount);
//       const dsTotal = toDec(item?.totalDsPerRow);
//       const extraAmount = toDec(
//         item?.finalData?.detailsJson?.[0]?.extraAmount ||
//           item?.extraAmount ||
//           0,
//       );
//       const isFixed =
//         item?.finalData?.detailsJson?.[0]?.fixedAmount === 'yes' ||
//         item?.fixedAmount === 'yes';

//       const baseAmount = isFixed ? net : net.minus(dsTotal);
//       // const baseAmount = isFixed ? net : net.minus(dsTotal).minus(extraAmount);

//       let taxable = baseAmount
//         .minus(discount)
//         .minus(purchaseA)
//         .minus(carryForward);

//       if (taxable.isNegative()) {
//         carryForward = carryForward.plus(taxable.abs());
//         taxable = new Decimal(0);
//       } else {
//         carryForward = new Decimal(0);
//       }

//       const taxPercent =
//         item?.itemType?.toLowerCase() !== 'silver' &&
//         item?.itemTypeName?.toLowerCase() !== 'silver'
//           ? item?.taxPercent
//           : isSilverNetGreaterThanThreshold
//             ? item?.taxPercent
//             : 0;
//       const tax = roundTo3(taxable?.times(taxPercent || 0));
//       const taxableToSend =
//         item?.itemType?.toLowerCase() !== 'silver' &&
//         item?.itemTypeName?.toLowerCase() !== 'silver'
//           ? taxable?.gt(0)
//             ? roundTo3(taxable)
//             : 0
//           : isSilverNetGreaterThanThreshold
//             ? taxable?.gt(0)
//               ? roundTo3(taxable)
//               : 0
//             : 0;
//       const vat = roundTo3(item?.totalDsPerRow * (item?.vatPercent || 0));

//       const netAmountWithVat = roundTo3(
//         net.plus(tax).plus(vat).minus(item.discountAmount),
//       );

//       return {
//         ...item,
//         taxableAmount: taxableToSend,
//         taxAmount: tax,
//         vatAmount: vat,
//         netAmountWithTax: taxable?.gt(0) ? roundTo3(taxable?.plus(tax)) : 0,
//         netAmountWithTaxPlusVat: netAmountWithVat < 0 ? 0 : netAmountWithVat,
//       };
//     })
//     .sort((a, b) => a._idx - b._idx)
//     .map(({ _idx, ...it }) => it);
// };

// const applyReceiveWeight = (items, receiveWeight) => {
//   let remaining = toDec(receiveWeight);

//   return items.map((item) => {
//     const netWastage = toDec(item.netWithWastage);
//     const rate = toDec(item.rate);
//     const isFixed = item?.finalData?.detailsJson?.[0]?.fixedAmount === 'yes';

//     let newNetAmount = new Decimal(0);
//     let receiveAmount = new Decimal(0);

//     if (netWastage.greaterThanOrEqualTo(remaining)) {
//       const diff = netWastage.minus(remaining);
//       newNetAmount = diff.times(rate);
//       receiveAmount = newNetAmount;
//       remaining = new Decimal(0);
//     } else {
//       const diff = remaining.minus(netWastage);
//       receiveAmount = diff.times(rate);
//       remaining = diff;
//       newNetAmount = new Decimal(0);
//     }

//     const making = roundTo3(item.makingAmount || 0);
//     const roundOff = roundTo3(
//       item?.finalData?.detailsJson?.[0]?.roundOff || item?.roundOff || 0,
//     );

//     const calculated = roundTo3(newNetAmount.plus(making).plus(roundOff));

//     const diamondStoneJson =
//       item?.diamondStones || item?.finalData?.diamondStoneJson || [];

//     const vatableAmount = diamondStoneJson.reduce(
//       (acc, it) => acc.plus(toDec(it?.amount)),
//       new Decimal(0),
//     );

//     return {
//       ...item,
//       totalDsPerRow: roundTo3(getDiamondStoneTotal(item)),
//       calculatableNetAmount: isFixed ? item.netAmount : calculated,
//       vatableAmount: roundTo3(vatableAmount),
//       receiveAmount: receiveAmount.gt(0) ? roundTo3(receiveAmount) : 0,
//     };
//   });
// };

// const distributeDiscountsAfterReceive = (items, totalDiscount) => {
//   const totalCalcNet = items.reduce(
//     (acc, it) => acc.plus(toDec(it.calculatableNetAmount)),
//     new Decimal(0),
//   );

//   return items.map((item) => {
//     const calcNet = toDec(item.calculatableNetAmount);
//     const ratio = totalCalcNet.isZero()
//       ? new Decimal(0)
//       : calcNet.div(totalCalcNet);
//     return { ...item, discountAmount: roundTo3(ratio.times(totalDiscount)) };
//   });
// };

// const calculateTaxAndVatAfterReceive = (items) => {
//   return items.map((item) => {
//     const calcNet = toDec(item.calculatableNetAmount);
//     const discount = toDec(item.discountAmount);

//     const taxable = calcNet.minus(discount);
//     const tax = roundTo3(taxable.times(item.taxPercent || 0));
//     const vat = roundTo3(
//       toDec(item.vatableAmount || 0).times(item.vatPercent || 0),
//     );

//     return {
//       ...item,
//       taxableAmount: taxable.gt(0) ? roundTo3(taxable) : 0,
//       taxAmount: tax,
//       vatAmount: vat,
//       netAmountWithTax: taxable.gt(0) ? roundTo3(taxable.plus(tax)) : 0,
//       netAmountWithTaxPlusVat: roundTo3(
//         taxable.minus(discount).plus(tax).plus(vat),
//       ),
//     };
//   });
// };

// const finalizeTotals = (items) => {
//   const sum = (key) =>
//     roundTo3(
//       items.reduce((acc, it) => acc.plus(toDec(it[key] || 0)), new Decimal(0)),
//     );

//   return {
//     items,
//     totalTaxableAmount: sum('taxableAmount'),
//     totalTaxAmount: sum('taxAmount'),
//     totalVatAmount: sum('vatAmount'),
//     totalReceiveAmount: sum('receiveAmount'),
//   };
// };

// export const calculateSalesAmountsWithTaxVatV3 = (
//   tableData,
//   totalDiscountAmount,
//   silverThreshold,
//   purchaseAmount,
//   receiveWeight,
// ) => {
//   const isPurchaseMode = purchaseAmount > 0;
//   const isReceiveMode = receiveWeight > 0;

//   let items = tableData;

//   if (isPurchaseMode) {
//     items = distributeDiscountsPurchaseMode(
//       items,
//       totalDiscountAmount,
//       purchaseAmount,
//       silverThreshold,
//     );
//     items = calculateTaxAndVat(items, silverThreshold);
//     return finalizeTotals(items);
//   }

//   if (isReceiveMode) {
//     items = applyReceiveWeight(items, receiveWeight);
//     items = distributeDiscountsAfterReceive(items, totalDiscountAmount);
//     items = calculateTaxAndVatAfterReceive(items);
//     return finalizeTotals(items);
//   }
//   // DEFAULT MODE (no purchase, no receive)
//   items = distributeDiscountsPurchaseMode(
//     items,
//     totalDiscountAmount,
//     0,
//     silverThreshold,
//   );
//   items = calculateTaxAndVat(items, silverThreshold);
//   return finalizeTotals(items);
// };

const toDec = (v) => new Decimal(v || 0);

export const calculateTotalAmount = (params) => {
  const {
    purity,
    netWeightWithWastage,
    makingAmount,
    diamondAmount,
    stoneAmount,
    extraAmount,
    rate,
    isReceiveFromSales = false,
    isCalculateOnPure = false,
    netWeight,
    wastageWeight,
  } = params;

  try {
    if (
      isNaN(makingAmount) ||
      isNaN(diamondAmount) ||
      isNaN(stoneAmount) ||
      isNaN(extraAmount)
    ) {
      throw new Error('Invalid input values');
    }
    const ratePerPurity = isReceiveFromSales
      ? rate
      : new Decimal(rate).mul(purity).div(100).toDecimalPlaces(3).toNumber();

    // let netWeightAmount = new Decimal(netWeight).mul(ratePerPurity).toNumber();

    // let wastageWeightAmount = new Decimal(wastageWeight).mul(rate).toNumber();

    let netWithWastageAmount;

    // if (isCalculateOnPure) {
    //   netWithWastageAmount = new Decimal(netWeightAmount).plus(
    //     wastageWeightAmount,
    //   );
    // } else {
    netWithWastageAmount = new Decimal(netWeightWithWastage)
      .mul(ratePerPurity)
      .toNumber();
    // }

    const total = new Decimal(netWithWastageAmount)
      .add(makingAmount)
      .add(diamondAmount)
      .add(stoneAmount)
      .add(extraAmount)
      .toDecimalPlaces(3)
      .toNumber();

    return total;
  } catch (error) {
    console.error(error);
    return 0;
  }
};

const getDiamondStoneTotal = (item) => {
  const stones = item?.finalData?.diamondStoneJson || item?.diamondStones || [];
  return stones.reduce((sum, s) => sum + (s?.amount || s?.salesAmount || 0), 0);
};

export const calculateSilverThreshold = (items, silverThreshold) => {
  const totalSilverSubTotal =
    items
      ?.filter(
        (item) =>
          item?.itemType?.toLowerCase() === 'silver' ||
          item?.itemTypeName?.toLowerCase() === 'silver',
      )
      ?.reduce(
        (acc, item) =>
          acc.plus(
            item?.finalData?.detailsJson?.[0]?.subTotal || item?.subTotal || 0,
          ),
        new Decimal(0),
      ) || new Decimal(0);

  const totalSilverDiamondStoneAmount =
    items
      ?.filter(
        (item) =>
          item?.itemType?.toLowerCase() === 'silver' ||
          item?.itemTypeName?.toLowerCase() === 'silver',
      )
      ?.reduce(
        (acc, item) => acc.plus(getDiamondStoneTotal(item)),
        new Decimal(0),
      ) || new Decimal(0);

  return {
    isSilverNetGreaterThanThreshold: totalSilverSubTotal
      // .minus(totalSilverItemExtraAmount)
      .minus(totalSilverDiamondStoneAmount)
      .greaterThanOrEqualTo(toDec(silverThreshold)),
    totalSilverNet: totalSilverSubTotal,
  };
};

const distributeDiscountsPurchaseMode = (
  items,
  totalDiscount,
  purchaseAmount,
  silverThreshold,
) => {
  const totalNet = items.reduce(
    (acc, it) => acc.plus(toDec(it.netAmount)),
    new Decimal(0),
  );

  const totalDiamondStoneAmount = items?.reduce((acc, item) => {
    const diamondStoneJson =
      item?.diamondStones || item?.finalData?.diamondStoneJson || [];
    const totalAmount = diamondStoneJson.reduce(
      (acc, it) => acc.plus(toDec(it?.amount)),
      new Decimal(0),
    );
    return acc.plus(totalAmount);
  }, new Decimal(0));

  const { isSilverNetGreaterThanThreshold, totalSilverNet } =
    calculateSilverThreshold(items, silverThreshold);

  const totalExtraAmount = items?.reduce((acc, item) => {
    const extraAmount =
      item?.extraAmount || item?.finalData?.detailsJson?.[0]?.extraAmount;
    return acc.plus(toDec(extraAmount));
  }, new Decimal(0));

  const calculatableTotalNetForDiscount = toDec(totalNet);

  // Item wise calculation discount amount
  const itemsWithDiscount = items.map((item) => {
    const extraAmount =
      item?.extraAmount || item?.finalData?.detailsJson?.[0]?.extraAmount;
    // const net = toDec(item.netAmount).minus(toDec(extraAmount));
    const net = toDec(item.netAmount);

    const diamondStoneJson =
      item?.diamondStones || item?.finalData?.diamondStoneJson || [];
    const dsAmount = diamondStoneJson.reduce(
      (acc, it) => acc.plus(toDec(it?.amount)),
      new Decimal(0),
    );

    const calculatableItemNetForDiscount = net || new Decimal(0);

    let ratioForDiscount = calculatableTotalNetForDiscount.isZero()
      ? new Decimal(0)
      : calculatableItemNetForDiscount.div(calculatableTotalNetForDiscount);

    return {
      ...item,
      totalDsPerRow: roundTo3(getDiamondStoneTotal(item)),
      discountAmount: roundTo3(ratioForDiscount.times(totalDiscount)),
      // purchaseAmount: roundTo3(ratioForPurchase.times(purchaseAmount)),
    };
  });

  const calculatableTotalNetForPurchase = itemsWithDiscount.reduce(
    (acc, item) => {
      const isSilver =
        item?.itemType?.toLowerCase() === 'silver' ||
        item?.itemTypeName?.toLowerCase() === 'silver';
      const net = toDec(item.netAmount);
      const discount = toDec(item?.discountAmount || 0);
      const diamondStoneJson =
        item?.diamondStones || item?.finalData?.diamondStoneJson || [];
      const dsAmount = diamondStoneJson.reduce(
        (acc, it) => acc.plus(toDec(it?.amount)),
        new Decimal(0),
      );

      const extraAmount = toDec(
        item?.extraAmount || item?.finalData?.detailsJson?.[0]?.extraAmount,
      );

      const baseNet = net.minus(dsAmount).minus(discount).minus(extraAmount);

      const calculatableItemNetForPurchase =
        isSilver && !isSilverNetGreaterThanThreshold ? new Decimal(0) : baseNet;

      return acc.plus(calculatableItemNetForPurchase);
    },
    new Decimal(0),
  );

  const itemsWithPurchase = itemsWithDiscount?.map((item) => {
    const isSilver =
      item?.itemType?.toLowerCase() === 'silver' ||
      item?.itemTypeName?.toLowerCase() === 'silver';
    const net = toDec(item.netAmount);
    const discount = toDec(item?.discountAmount || 0);
    const diamondStoneJson =
      item?.diamondStones || item?.finalData?.diamondStoneJson || [];
    const dsAmount = diamondStoneJson.reduce(
      (acc, it) => acc.plus(toDec(it?.amount)),
      new Decimal(0),
    );
    const extraAmount = toDec(
      item?.extraAmount || item?.finalData?.detailsJson?.[0]?.extraAmount,
    );

    const baseNet = net.minus(dsAmount).minus(discount).minus(extraAmount);

    const calculatableItemNetForPurchase =
      isSilver && !isSilverNetGreaterThanThreshold ? new Decimal(0) : baseNet;

    let ratioForPurchase = calculatableTotalNetForPurchase.isZero()
      ? new Decimal(0)
      : calculatableItemNetForPurchase.div(calculatableTotalNetForPurchase);

    return {
      ...item,
      purchaseAmount: roundTo3(ratioForPurchase.times(purchaseAmount)),
    };
  });

  return itemsWithPurchase;
};

const calculateTaxAndVat = (items, silverThreshold) => {
  let carryForward = new Decimal(0);

  const { isSilverNetGreaterThanThreshold } = calculateSilverThreshold(
    items,
    silverThreshold,
  );

  return items
    .map((item, i) => ({ ...item, _idx: i }))
    .sort((a, b) => a.netAmount - b.netAmount)
    .map((item) => {
      const net = toDec(item?.netAmount);
      const discount = toDec(item?.discountAmount);
      const dsTotal = toDec(item?.totalDsPerRow);
      const extraAmount = toDec(
        item?.finalData?.detailsJson?.[0]?.extraAmount ||
          item?.extraAmount ||
          0,
      );
      const isFixed =
        item?.finalData?.detailsJson?.[0]?.fixedAmount === 'yes' ||
        item?.fixedAmount === 'yes';

      // const baseAmount = isFixed ? net : net.minus(dsTotal);
      const baseAmount = isFixed && !item?.posFixed ? net : net.minus(dsTotal);

      let taxable = baseAmount.minus(discount).minus(carryForward);

      if (taxable.isNegative()) {
        carryForward = carryForward.plus(taxable.abs());
        taxable = new Decimal(0);
      } else {
        carryForward = new Decimal(0);
      }

      const isSilver =
        item?.itemType?.toLowerCase() === 'silver' ||
        item?.itemTypeName?.toLowerCase() === 'silver';

      const taxPercent =
        !isSilver || isSilverNetGreaterThanThreshold ? item?.taxPercent : 0;

      const tax = roundTo3(taxable?.times(taxPercent || 0));

      const shouldApplyTax = !isSilver || isSilverNetGreaterThanThreshold;

      const taxableToSend =
        shouldApplyTax && taxable?.gt(0) ? roundTo3(taxable) : 0;
      const vat = roundTo3(item?.totalDsPerRow * (item?.vatPercent || 0));

      const netAmountWithVat = roundTo3(
        net.plus(tax).plus(vat).minus(item.discountAmount),
      );

      return {
        ...item,
        taxableAmount: taxableToSend,
        taxAmount: tax,
        vatAmount: vat,
        netAmountWithTax: taxable?.gt(0) ? roundTo3(taxable?.plus(tax)) : 0,
        netAmountWithTaxPlusVat: netAmountWithVat < 0 ? 0 : netAmountWithVat,
      };
    })
    .sort((a, b) => a._idx - b._idx)
    .map(({ _idx, ...it }) => it);
};

const applyReceiveWeight = (items, receiveWeight) => {
  let remainingGold = toDec(receiveWeight?.gold?.pureWt || 0).plus(
    receiveWeight?.gold?.wastagePureWt || 0,
  );
  let remainingSilver = toDec(receiveWeight?.silver?.pureWt || 0).plus(
    receiveWeight?.silver?.wastagePureWt || 0,
  );

  return items.map((item) => {
    const pureWt = toDec(item?.pureWeight);

    const wastageWt = toDec(item?.wastageWeight);
    const purity = toDec(item?.purity);
    const wastagePureWt = item?.calculateOnPure
      ? wastageWt
      : (wastageWt * purity) / 100;
    // const pureWastage = pureWt?.plus(wastagePureWt);
    const pureWastage = pureWt;

    const netWastage = toDec(item?.netWithWastage).isZero()
      ? toDec(roundTo3(item?.netWeight)).plus(wastageWt)
      : toDec(item?.netWithWastage);

    const rate = toDec(item?.rate);
    const isFixed =
      item?.finalData?.detailsJson?.[0]?.fixedAmount === 'yes' ||
      !!item?.posFixed;
    const making = roundTo3(item.makingAmount || 0);
    const roundOff = roundTo3(item?.finalData?.detailsJson?.[0]?.roundOff || 0);
    const diamondStoneJson =
      item?.diamondStones || item?.finalData?.diamondStoneJson || [];
    const diamondAmount = diamondStoneJson
      ?.filter(
        (item) =>
          item?.itemTypeName?.toLowerCase() === 'diamond' ||
          item?.isName?.toLowerCase() === 'diamond',
      )
      ?.reduce((acc, item) => acc.plus(item?.amount), new Decimal(0))
      .toNumber();
    const stoneAmount = diamondStoneJson
      ?.filter(
        (item) =>
          item?.itemTypeName?.toLowerCase() === 'stone' ||
          item?.isName?.toLowerCase() === 'stone',
      )
      ?.reduce((acc, item) => acc.plus(item?.amount), new Decimal(0))
      .toNumber();
    const extraAmount = roundTo3(
      item?.finalData?.detailsJson?.[0]?.extraAmount || item?.extraAmount || 0,
    );
    const vatableAmount = diamondStoneJson.reduce(
      (acc, it) => acc.plus(toDec(it?.amount)),
      new Decimal(0),
    );
    const itemType =
      item?.itemType ||
      item?.itemTypeName ||
      item?.finalData?.detailsJson?.[0]?.itemType ||
      item?.finalData?.detailsJson?.[0]?.itemTypeName;
    const isGoldItem = itemType?.toLowerCase() === 'gold';
    const isSilverItem = itemType?.toLowerCase() === 'silver';

    let newNetAmount = 0;
    let receiveMetalWt = 0;

    // Pick the correct metal pool based on item type
    const isMetalItem = isGoldItem || isSilverItem;
    // Receive metal for fifo structure
    let remaining = isGoldItem
      ? remainingGold
      : isSilverItem
        ? remainingSilver
        : new Decimal(0);

    if (isMetalItem) {
      if (remaining.lessThanOrEqualTo(0)) {
        receiveMetalWt = new Decimal(0);
        newNetAmount = calculateTotalAmount({
          purity,
          netWeightWithWastage: netWastage,
          makingAmount: making,
          diamondAmount,
          stoneAmount,
          extraAmount,
          rate,
          isCalculateOnPure: item?.calculateonPure,
        });
      } else if (pureWastage.greaterThanOrEqualTo(remaining)) {
        const diff = pureWastage.minus(remaining);
        receiveMetalWt = toDec(remaining);
        remaining = new Decimal(0);
        newNetAmount = calculateTotalAmount({
          purity,
          netWeightWithWastage: diff?.plus(wastagePureWt),
          makingAmount: making,
          diamondAmount,
          stoneAmount,
          extraAmount,
          rate,
          isReceiveFromSales: true,
          isCalculateOnPure: item?.calculateonPure,
        });
      } else {
        const diff = remaining.minus(pureWastage);
        remaining = diff;
        receiveMetalWt = pureWastage;
        newNetAmount = calculateTotalAmount({
          purity,
          netWeightWithWastage: wastagePureWt,
          makingAmount: making,
          diamondAmount,
          stoneAmount,
          extraAmount,
          rate,
          isReceiveFromSales: true,
          isCalculateOnPure: item?.calculateonPure,
        });
      }
      // Write the updated remaining back to the correct pool
      if (isGoldItem) remainingGold = remaining;
      else if (isSilverItem) remainingSilver = remaining;
    } else {
      // Non-metal item — no receive weight applied, calculate normally
      newNetAmount = calculateTotalAmount({
        purity,
        netWeightWithWastage: netWastage,
        makingAmount: making,
        diamondAmount,
        stoneAmount,
        extraAmount,
        rate,
        isCalculateOnPure: item?.calculateonPure,
      });
    }

    const pureWastageToSend = pureWastage?.plus(wastagePureWt);
    return {
      ...item,
      totalDsPerRow: roundTo3(getDiamondStoneTotal(item)),
      calculatableNetAmount: isFixed ? item.netAmount : newNetAmount,
      netAmount: isFixed ? item.netAmount : newNetAmount,
      vatableAmount: roundTo3(vatableAmount),
      pureWastage: roundTo3(pureWastageToSend),
      receiveWeight: roundTo3(receiveMetalWt),
      finalWeight: roundTo3(pureWastageToSend - receiveMetalWt),
      finalWeightToShow: roundTo3(pureWastage - receiveMetalWt),
    };
  });
};

const finalizeTotals = (items) => {
  const sum = (key) =>
    roundTo3(
      items.reduce((acc, it) => acc.plus(toDec(it[key] || 0)), new Decimal(0)),
    );

  return {
    items,
    totalTaxableAmount: sum('taxableAmount'),
    totalTaxAmount: sum('taxAmount'),
    totalVatAmount: sum('vatAmount'),
  };
};

const fixedNum = (v) => Number(String(v ?? '').replace(/,/g, '')) || 0;

const isDiamondEntry = (s) =>
  (s?.itemTypeName || s?.isName || '').toLowerCase() === 'diamond';

const isFixedDsEntry = (s) => !!(s?.itemId || s?.typeId);

const fixedDsAnchorIndex = (entries = []) => {
  const diamond = entries.findIndex(
    (s) => isFixedDsEntry(s) && isDiamondEntry(s),
  );
  return diamond >= 0 ? diamond : entries.findIndex(isFixedDsEntry);
};

export const clearFixedDsExceptAnchor = (entries = []) => {
  const anchor = fixedDsAnchorIndex(entries);
  return entries.map((s, i) =>
    isFixedDsEntry(s) && i !== anchor
      ? { ...s, rateOn: 'bulk', amount: 0, salesAmount: 0, salesRate: 0 }
      : s,
  );
};

export const anchorFixedDs = (entries = [], dsTotal = 0) => {
  const anchor = fixedDsAnchorIndex(entries);
  if (anchor < 0) return entries;
  const othersSum = entries.reduce(
    (a, s, i) =>
      isFixedDsEntry(s) && i !== anchor ? a + fixedNum(s?.amount) : a,
    0,
  );
  const amount = Math.max(roundTo3(dsTotal - othersSum), 0);
  return entries.map((s, i) =>
    i === anchor
      ? { ...s, rateOn: 'bulk', amount, salesAmount: amount, salesRate: amount }
      : s,
  );
};

export const computeSalesFixedSplit = ({
  mrp,
  rate,
  purity,
  netWithWastage,
  netWeight,
  wastageWeight,
  pureWeight,
  isPure,
  makingAmount,
  roundOff,
  extraAmount,
  taxRate,
  vatRate,
  entries = [],
}) => {
  const totWt =
    fixedNum(netWithWastage) || fixedNum(netWeight) + fixedNum(wastageWeight);
  const metal = isPure
    ? roundTo3(
        (fixedNum(pureWeight) + fixedNum(wastageWeight)) * fixedNum(rate),
      )
    : roundTo3(totWt * roundTo3((fixedNum(rate) * fixedNum(purity)) / 100));
  const extra = fixedNum(extraAmount);
  const round = fixedNum(roundOff);
  const hasDs = entries.some(isFixedDsEntry);
  const making = hasDs
    ? fixedNum(makingAmount)
    : Math.max(
        roundTo3(
          (fixedNum(mrp) - extra) / (1 + fixedNum(taxRate)) - metal - round,
        ),
        0,
      );
  const taxBase = roundTo3(metal + making + round);
  const tax = roundTo3(taxBase * fixedNum(taxRate));
  const dsTotal = hasDs
    ? Math.max(
        roundTo3(
          (fixedNum(mrp) - taxBase - extra - tax) / (1 + fixedNum(vatRate)),
        ),
        0,
      )
    : 0;
  const anchored = hasDs ? anchorFixedDs(entries, dsTotal) : entries;
  const valid = anchored.filter(isFixedDsEntry);
  const sumBy = (pred) =>
    roundTo3(valid.filter(pred).reduce((a, s) => a + fixedNum(s?.amount), 0));
  return {
    making,
    dsTotal,
    entries: anchored,
    diamondAmount: sumBy(isDiamondEntry),
    stoneAmount: sumBy((s) => !isDiamondEntry(s)),
    preTaxNet: roundTo3(taxBase + extra + dsTotal),
  };
};

export const isSalesFixedRow = (row) => {
  const detail = row?.finalData?.detailsJson?.[0];
  return (
    row?.isMrp === true ||
    detail?.isMrp === true ||
    row?.fixedAmount === 'yes' ||
    detail?.fixedAmount === 'yes'
  );
};

export const withSalesFixedSplit = (row) => {
  if (!isSalesFixedRow(row)) return row;
  const detail = row?.finalData?.detailsJson?.[0] || row;
  const pick = (key) => detail?.[key] ?? row?.[key];
  const mrp = fixedNum(pick('fixedNetAmount') ?? pick('netAmount'));
  const split = computeSalesFixedSplit({
    mrp,
    rate: pick('rate'),
    purity: pick('purity'),
    netWithWastage: pick('netWithWastage'),
    netWeight: pick('netWeight'),
    wastageWeight: pick('wastageWeight'),
    pureWeight: pick('pureWeight'),
    isPure: !!(detail?.isPure ?? row?.isPure ?? detail?.calculateOnPure),
    makingAmount: pick('makingAmount'),
    roundOff: pick('roundOff'),
    extraAmount: pick('extraAmount'),
    taxRate: row?.taxPercent,
    vatRate: row?.vatPercent,
    entries: (
      row?.finalData?.diamondStoneJson ||
      row?.diamondStones ||
      []
    ).filter(isFixedDsEntry),
  });
  const fixedFields = {
    fixedAmount: 'yes',
    fixedNetAmount: mrp,
    netAmount: split.preTaxNet,
    subTotal: split.preTaxNet,
    makingAmount: split.making,
    diamondAmount: split.diamondAmount,
    stoneAmount: split.stoneAmount,
  };

  return {
    ...row,
    ...fixedFields,
    posFixed: true,
    diamondStones: split.entries,
    ...(row?.finalData && {
      finalData: {
        ...row.finalData,
        diamondStoneJson: split.entries,
        ...(row.finalData.detailsJson?.length > 0 && {
          detailsJson: [
            { ...detail, ...fixedFields },
            ...row.finalData.detailsJson.slice(1),
          ],
        }),
      },
    }),
  };
};

export const calculateSalesAmountsWithTaxVatV3 = (
  tableData,
  totalDiscountAmount,
  purchaseAmount,
  silverThreshold,
  receiveMetalJson,
) => {
  try {
    const isReceiveGoldWt = receiveMetalJson?.gold?.pureWt > 0;
    const isReceiveSilverWt = receiveMetalJson?.silver?.pureWt > 0;
    const isReceiveMode = isReceiveGoldWt || isReceiveSilverWt;
    let items = tableData;

    if (isReceiveMode) {
      items = applyReceiveWeight(items, receiveMetalJson);
    }

    items = distributeDiscountsPurchaseMode(
      items,
      totalDiscountAmount,
      purchaseAmount || 0,
      silverThreshold,
    );
    items = calculateTaxAndVat(items, silverThreshold);
    return finalizeTotals(items);
  } catch (error) {
    console.log(error);
    return tableData;
  }
};
